import React, { useCallback, useMemo, useState } from "react";
import { SelectItem } from "../../components/inputs/SelectInput";
import { useMeetingStore } from "../../stores/useMeetingStore";
import { toString } from "../../utilities/stringify";
import { useActiveTopicStore } from "../../stores/useActiveTopicStore";
import { ITopicTask } from "../../api/ITopicTask";
import ISubTopic from "../../api/ISubTopic";
import useDeleteTask from "../api/useDeleteTask";
import useShowError from "../utils/useShowError";
import useTaskForm from "./useTaskForm";
import { ShownTaskForm, useTaskFormStore } from "../../stores/useTaskFormStore";

const SortingOptions = ["Points Asc", "Points Desc", "Title Asc", "Title Desc", "Jira Key"];

/**
 * Natural sort comparator: sorts strings by splitting into text and numeric segments.
 * Case-insensitive. "PROJ-2" comes before "PROJ-10".
 */
const naturalCompare = (a: string, b: string): number => {
    const aLower = a.toLowerCase();
    const bLower = b.toLowerCase();

    const re = /(\d+|\D+)/g;
    const aParts = aLower.match(re) || [];
    const bParts = bLower.match(re) || [];

    const len = Math.min(aParts.length, bParts.length);
    for (let i = 0; i < len; i++) {
        const aPart = aParts[i];
        const bPart = bParts[i];

        const aNum = Number(aPart);
        const bNum = Number(bPart);

        if (!isNaN(aNum) && !isNaN(bNum)) {
            if (aNum !== bNum) return aNum - bNum;
        } else {
            if (aPart < bPart) return -1;
            if (aPart > bPart) return 1;
        }
    }

    return aParts.length - bParts.length;
};

const useTaskList = () => {
    const meeting = useMeetingStore((state) => state.meeting);
    const { activeSubTopic, activeTask, setActive } = useActiveTopicStore();
    const { form, setOpen, setForm } = useTaskFormStore();

    const [sortBy, setSortBy] = useState("");

    const {
        error,
        setError,
        clearError,
        remove: _removeTask,
    } = useDeleteTask();
    useShowError(error, clearError);

    const sortOptions: SelectItem[] = SortingOptions.map((option) => ({
        key: option,
        value: option,
        placeHolder: option,
    }));

    const rawTasks = meeting?.subTopics ?? [];

    const tasks = useMemo(() => {
        const sorted = [...rawTasks];
        switch (sortBy) {
            case "Points Asc":
                sorted.sort((a, b) => (a.points ?? 0) - (b.points ?? 0));
                break;
            case "Points Desc":
                sorted.sort((a, b) => (b.points ?? 0) - (a.points ?? 0));
                break;
            case "Title Asc":
                sorted.sort((a, b) => naturalCompare(a.topic, b.topic));
                break;
            case "Title Desc":
                sorted.sort((a, b) => naturalCompare(b.topic, a.topic));
                break;
            case "Jira Key":
                sorted.sort((a, b) =>
                    naturalCompare(a.jiraKey ?? "", b.jiraKey ?? "")
                );
                break;
            default:
                break;
        }
        return sorted;
    }, [rawTasks, sortBy]);

    const onSortChange = useCallback(
        (item: SelectItem) => {
            setSortBy(item.value);
        },
        []
    );

    const active: ITopicTask | ISubTopic | undefined =
        activeTask ?? activeSubTopic;

    const removeTask = useCallback(
        (task: ISubTopic | ITopicTask) => {
            if (!meeting) return setError(`Missing Meeting`);
            _removeTask(meeting.id, task);
        },
        [meeting?.id, _removeTask]
    );

    const editTask = useCallback(
        (subTopic: ISubTopic, task?: ITopicTask) => {
            setActive(subTopic, task);
            setForm(task ? ShownTaskForm.UpdateChild : ShownTaskForm.Update);
            setOpen(true);
        },
        [setActive]
    );

    const cardPress = useCallback(
        (subTopic?: ISubTopic, task?: ITopicTask) => {
            setActive(subTopic, task);
            if (form !== ShownTaskForm.Add && form !== ShownTaskForm.JiraAdd)
                setOpen(false);
        },
        [form, setActive, setOpen]
    );

    const onSubTopicAdd = useCallback(
        (item: ISubTopic | ITopicTask) => {
            setActive(item as ISubTopic, undefined);
            setForm(ShownTaskForm.AddChild);
            setOpen(true);
        },
        [setActive, setForm, setOpen]
    );
    const onSubTopicEdit = useCallback(
        (item: ISubTopic | ITopicTask) => {
            editTask(item as ISubTopic, undefined);
        },
        [editTask]
    );
    const onSubTopicPress = useCallback(
        (item: ISubTopic | ITopicTask) => {
            cardPress(
                activeSubTopic?.id === item.id && activeTask === undefined
                    ? undefined
                    : (item as ISubTopic),
                undefined
            );
        },
        [cardPress, activeSubTopic?.id, activeTask?.id]
    );

    const onTaskPress = useCallback(
        (subTopic: ISubTopic) => (item: ITopicTask) => {
            activeTask?.id === item.id
                ? cardPress(undefined, undefined)
                : cardPress(subTopic, item);
        },
        [cardPress, activeTask?.id]
    );
    const onTaskEdit = useCallback(
        (subTopic: ISubTopic) => (item: ITopicTask) => {
            editTask(subTopic, item);
        },
        [editTask]
    );
    const onTaskDelete = useCallback(
        (item: ITopicTask) => {
            removeTask(item);
        },
        [removeTask]
    );

    const calculatePoints = useCallback((topics: ISubTopic[]) => {
        let totalPoints = 0;
        for (let subTopic of topics) {
            if (subTopic.points === undefined || subTopic.points === null)
                continue;
            totalPoints += subTopic.points;

            for (let task of subTopic.tasks) {
                if (task.points === undefined || task.points === null) continue;
                totalPoints += task.points;
            }
        }
        return totalPoints;
    }, []);

    return {
        sortOptions,
        sortBy,
        tasks,
        active,
        totalPoints: calculatePoints(tasks),
        setActive,
        onSortChange,
        onSubTopicPress,
        onSubTopicAdd,
        onSubTopicEdit,
        onTaskPress,
        onTaskEdit,
        onTaskDelete,
    };
};

export default useTaskList;
